import React, { useEffect, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PRIORITIES } from './taskShared';

const EMPTY = { title: '', priority: 3, assignee_id: '', helper_ids: [], due_date: '', due_time: '', subtasks: [] };

// Alta / edición de tarea: qué, prioridad (color), responsable, apoyo, fecha y hora límite, subtareas.
export const TaskDialog = ({ open, onClose, task, employees, onSaved, canDelete, onDelete, readOnly = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [sub, setSub] = useState('');
  const [saving, setSaving] = useState(false);
  const isEdit = !!task?.task_id;

  useEffect(() => {
    if (!open) return;
    setSub('');
    setForm(task ? { title: task.title, priority: task.priority, assignee_id: task.assignee_id || '', helper_ids: task.helper_ids || [], due_date: task.due_date || '', due_time: task.due_time || '', subtasks: task.subtasks || [] } : EMPTY);
  }, [open, task]);

  const p = PRIORITIES[form.priority];
  const addSub = () => { const t = sub.trim(); if (!t) return; setForm({ ...form, subtasks: [...form.subtasks, { title: t, done: false }] }); setSub(''); };
  const toggleSub = (i) => setForm({ ...form, subtasks: form.subtasks.map((s, k) => (k === i ? { ...s, done: !s.done } : s)) });
  const removeSub = (i) => setForm({ ...form, subtasks: form.subtasks.filter((_, k) => k !== i) });
  const addHelper = (id) => { if (!id || id === 'none' || form.helper_ids.includes(id) || id === form.assignee_id) return; setForm({ ...form, helper_ids: [...form.helper_ids, id] }); };
  const empName = (id) => employees.find((e) => e.employee_id === id)?.full_name || '—';

  const save = async () => {
    if (!form.title.trim()) { toast.error('Escribe qué hay que hacer'); return; }
    setSaving(true);
    try {
      const body = { ...form, title: form.title.trim(), assignee_id: form.assignee_id || null, due_date: form.due_date || null, due_time: form.due_time || null };
      const { data } = isEdit ? await api.put(`/tasks/${task.task_id}`, body) : await api.post('/tasks', body);
      toast.success(isEdit ? 'Tarea actualizada' : 'Tarea agregada');
      onSaved?.(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar la tarea'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{isEdit ? (readOnly ? 'Tarea' : 'Editar tarea') : 'Nueva tarea'}</DialogTitle>
          <DialogDescription className="sr-only">Formulario de tarea</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="task-title">¿Qué hay que hacer?</Label>
            <Input id="task-title" value={form.title} maxLength={200} disabled={readOnly} onChange={(e) => setForm({ ...form, title: e.target.value })} autoFocus data-testid="task-title-input" />
          </div>
          <div className="grid gap-1.5">
            <Label>Prioridad</Label>
            <Select value={String(form.priority)} onValueChange={(v) => setForm({ ...form, priority: Number(v) })} disabled={readOnly}>
              <SelectTrigger className={`font-semibold ${p.chip} border-0`} data-testid="task-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)} data-testid={`task-priority-${n}`}><span className={`mr-2 inline-block h-2.5 w-2.5 rounded-full ${PRIORITIES[n].chip.split(' ')[0]}`} />{PRIORITIES[n].label} · {PRIORITIES[n].name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">1: menos prioritario · 5: más prioritario</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Responsable</Label>
              <Select value={form.assignee_id || 'none'} onValueChange={(v) => setForm({ ...form, assignee_id: v === 'none' ? '' : v, helper_ids: form.helper_ids.filter((h) => h !== v) })} disabled={readOnly}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Elegir miembro" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Apoyo <span className="font-normal text-slate-400">(opcional)</span></Label>
              <Select value="none" onValueChange={addHelper} disabled={readOnly}>
                <SelectTrigger data-testid="task-helper-select"><SelectValue placeholder="+ Agregar apoyo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">+ Agregar apoyo</SelectItem>
                  {employees.filter((e) => e.employee_id !== form.assignee_id && !form.helper_ids.includes(e.employee_id)).map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.helper_ids.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="task-helpers">
                  {form.helper_ids.map((h) => <span key={h} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">{empName(h)}{!readOnly && <button type="button" onClick={() => setForm({ ...form, helper_ids: form.helper_ids.filter((x) => x !== h) })} className="text-slate-400 hover:text-rose-600"><X className="h-3 w-3" /></button>}</span>)}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5"><Label htmlFor="task-due">Fecha límite</Label><Input id="task-due" type="date" value={form.due_date} disabled={readOnly} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-date" /></div>
            <div className="grid gap-1.5"><Label htmlFor="task-time">Hora límite</Label><Input id="task-time" type="time" value={form.due_time} disabled={readOnly} onChange={(e) => setForm({ ...form, due_time: e.target.value })} data-testid="task-due-time" /></div>
          </div>
          <div className="grid gap-1.5">
            <Label>Subtareas <span className="font-normal text-slate-400">(opcional)</span></Label>
            {form.subtasks.length > 0 && (
              <ul className="space-y-1" data-testid="task-subtasks">
                {form.subtasks.map((s, i) => (
                  <li key={s.id || i} className="flex items-center gap-2 rounded-md bg-slate-50 px-2 py-1.5 text-sm">
                    <input type="checkbox" checked={!!s.done} onChange={() => toggleSub(i)} className="h-4 w-4 accent-[#0f1f4b]" data-testid={`task-subtask-check-${i}`} />
                    <span className={`flex-1 ${s.done ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{s.title}</span>
                    {!readOnly && <button type="button" onClick={() => removeSub(i)} className="text-slate-400 hover:text-rose-600" data-testid={`task-subtask-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                  </li>
                ))}
              </ul>
            )}
            {!readOnly && (
              <div className="flex gap-2">
                <Input value={sub} onChange={(e) => setSub(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSub(); } }} placeholder="Ej. Juntar recibos" maxLength={150} data-testid="task-subtask-input" />
                <Button type="button" variant="outline" onClick={addSub} disabled={!sub.trim()} className="gap-1 shrink-0" data-testid="task-subtask-add"><Plus className="h-4 w-4" />Agregar</Button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div>{isEdit && canDelete && <Button type="button" variant="ghost" onClick={() => onDelete?.(task)} className="gap-2 text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-testid="task-delete-btn"><Trash2 className="h-4 w-4" />Eliminar</Button>}</div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="task-cancel-btn">{readOnly ? 'Cerrar' : 'Cancelar'}</Button>
            {!readOnly && <Button type="button" onClick={save} disabled={saving || !form.title.trim()} className="bg-[#0f1f4b] hover:bg-[#1b2f6b]" data-testid="task-save-btn">{saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Agregar tarea'}</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDialog;
